import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { trackEvent } from "../hooks/useTracking";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function trackApiCall(url: string, method: string, startTime: number, status: number, error?: string) {
  const duration = Math.round(performance.now() - startTime);
  if (status >= 400) {
    trackEvent('api_error', { endpoint: url, method, status_code: status, error_message: error, duration_ms: duration });
  }
  if (duration > 2000) {
    trackEvent('slow_api_call', { endpoint: url, method, duration_ms: duration, status_code: status });
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const start = performance.now();
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  trackApiCall(url, method, start, res.status);
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey.join("/") as string;
    const start = performance.now();
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    trackApiCall(url, "GET", start, res.status);
    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
